<template>
  <SogPage
    id="page"
    title="Versicherungskarte"
  >
    <InsuranceCardComponent
      :insurance="_insurance"
      :skeleton-visible="_loadDetails"
    >
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          v-show="_isPrintVisible"
          ref="btnSearch"
          mdi-icon="mdi-printer"
          tooltip="Versicherungsbestätigung mit VN"
          @click="onPrint(true)"
        />
        <SogCardActionButton
          mdi-icon="mdi-paperclip-plus"
          tooltip="Anhang hinzufügen"
          @click="_uploadTempFile = true"
        />
        <SogCardActionButton
          mdi-icon="mdi-camera"
          tooltip="Anhang fotografieren"
          @click="onTakePhoto"
        />
        <SogCardActionButton
          v-show="_cancelVisible"
          mdi-icon="mdi-shield-off"
          tooltip="Versicherungskarte stornieren."
          @click="onCancel"
        />
        <SogCardActionMoreButton
          :items="_menuItems"
          @select="onMoreSelect"
        />
        <SogCardActionButton :kind="SogButtonKind.Back" />
      </SogCardActions>
    </InsuranceCardComponent>

    <TempFileFormComponent
      v-show="_uploadTempFile"
      ref="_uploader"
      v-model="_tempFileInfo"
    >
      <SogCardActions>
        <SogCardActionButton
          v-show="_tempFileInfo"
          mdi-icon="mdi-upload"
          @click="onImport"
        >
          importieren
        </SogCardActionButton>
        <SogCardActionButton
          :kind="SogButtonKind.Cancel"
          @click="
            _uploadTempFile = false;
            _camera = false;
          "
        />
      </SogCardActions>
    </TempFileFormComponent>
    <SogCard v-show="_insurance.vehicleRegistrationId">
      <SogCardTitle>Zulassungsdaten</SogCardTitle>
      <SogCardContent>
        <VehicleRegistrationComponent
          :data="_vehicleRegistration"
          visible-evb="false"
        />
      </SogCardContent>
    </SogCard>
    <SogCard v-show="_loadHistory || _history.length > 0">
      <SogCardTitle>Historie</SogCardTitle>
      <SogCardContent>
        <HistoryListComponent :data="_history" />
      </SogCardContent>
      <SogCardContent>
        <SogSkeleton v-show="_loadHistory" />
      </SogCardContent>
    </SogCard>
    <SogCard v-show="_loadFiles || _tempFile.length !== 0">
      <SogCardTitle>Anhänge</SogCardTitle>
      <SogCardContent
        v-for="file in _tempFile"
        :key="file.id"
      >
        <ItemCardComponent :file="file">
          <SogCardActionButton
            mdi-icon="mdi-delete"
            @click="onDelete(file)"
          />
          <SogCardActionButton
            mdi-icon="mdi-download"
            @click="onDownload(file)"
          />
        </ItemCardComponent>
      </SogCardContent>
      <SogCardContent>
        <SogSkeleton v-show="_loadFiles" />
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import {computed, onMounted, reactive, ref, watch} from "vue";
import {onBeforeRouteUpdate, useRouter} from "vue-router";
import Insurance from "@/shared/model/Insurance";
import {useNavigationStore} from "@/modules/Navigation/store";
import apiInsurance from "@/modules/Insurance/api";
import InsuranceCardComponent from "@/modules/Insurance/components/InsuranceCardComponent.vue";
import {useSpinner} from "@/shared/composables/SogSpinner";
import VehicleRegistration from "@/shared/model/VehicleRegistration";
import apiVehicleRegistration from "@/modules/VehicleRegistration/api";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import apiFile from "@/shared/services/apiFile";
import {storeToRefs} from "pinia";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import VehicleRegistrationComponent
  from "@/modules/VehicleRegistration/components/VehicleRegistrationFullComponent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import notificationService from "@/shared/services/NotificationService";
import TempFileFormComponent from "@/modules/FileUtils/components/TempFileFormComponent.vue";
import apiFileUtils from "@/modules/FileUtils/api";
import ListFile from "@/shared/model/ListFile";
import ItemCardComponent from "@/modules/FileUtils/components/FileCardComponent.vue";
import HistoryListComponent from "@/modules/History/components/HistoryListComponent.vue";
import apiHistory from "@/modules/History/api";
import History from "@/shared/model/History";
import getGuid from "@/shared/utils/getGuid";
import SogSkeleton from "@/shared/ui/SogSkeleton.vue";
import {InsuranceType} from "@/shared/model/insuranceType";
import {useAccountStore} from "@/stores/accountStore";
import SogCardActionMoreButton, {IActionItem} from "@/shared/ui/SogCardActionMoreButton.vue";
import {mdiPrinter} from "@mdi/js";
import {addDays} from "@/shared/utils/DateUtils";

interface Props {
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
    id: undefined
  }
)


const _insurance = ref<Insurance>(new Insurance());
const _vehicleRegistration = ref<VehicleRegistration | undefined>(undefined);
const _navigationStore = useNavigationStore();
const _accountStore = useAccountStore();
const {currentInsuranceId} = storeToRefs(_navigationStore);
const _router = useRouter();
const _tempFileInfo = ref<{ id?: string; fileName?: string; name?: string } | undefined>(undefined);
const _uploadTempFile = ref(false);
const _uploader = ref<InstanceType<typeof TempFileFormComponent> | null>(null);
const _camera = ref(false);
const _tempFile = ref<ListFile[]>([]);
const _spinner = useSpinner();
const _history: History[] = reactive<History[]>([]);
const insuranceId = ref<string | undefined>(undefined);
const _key = ref<string | undefined>(undefined);
const _loadHistory = ref(true);
const _loadFiles = ref(true);
const _loadDetails = ref(true);
const _menuItems: IActionItem[] = reactive([
  {
    text: 'ohne VN',
    id: "printWithout",
    svgIcon: mdiPrinter,
    visible: true
  },
])

const _isPrintVisible = computed(() => {
  return (
    _insurance.value.insuranceType !== InsuranceType.Zoll ||
    _accountStore.isRoot
  );
});

watch(_isPrintVisible, () => {
  _menuItems[0].visible = _isPrintVisible.value
})

async function p_init() {
  if (!_navigationStore.currentInsuranceId && !props.id) {
    _router.back();
    return;
  }
  insuranceId.value = props.id ?? _navigationStore.currentInsuranceId;
  _key.value = getGuid();
  const t_loadDetails = loadDetails(_key.value);
  const t_loadHistory = loadHistory(_key.value);
  const t_loadFiles = loadFiles(_key.value);
  await Promise.all([t_loadDetails, t_loadFiles, t_loadHistory]);
}


async function loadDetails(key: string) {
  if (!insuranceId.value) {
    return;
  }
  try {
    _loadDetails.value = true;
    const res = await apiInsurance.load(insuranceId.value);
    if (_key.value !== key) {
      return;
    }
    if (res) {
      _insurance.value = res.insurance;
      if (_insurance.value?.vehicleRegistrationId) {
        const vr = await apiVehicleRegistration.load(
          _insurance.value.vehicleRegistrationId
        );
        _vehicleRegistration.value = vr?.vehicleRegistration;
      }
    }
  } finally {
    _loadDetails.value = false;
  }
}

async function loadHistory(key: string) {
  if (!insuranceId.value) {
    return;
  }
  try {
    _loadHistory.value = true;
    _history.length = 0;
    const listHistory = await apiHistory.listInsurance(insuranceId.value);
    if (_key.value !== key) {
      return;
    }
    if (listHistory) _history.push(...listHistory);
  } finally {
    _loadHistory.value = false;
  }
}

async function loadFiles(key: string) {
  if (!insuranceId.value) {
    return;
  }
  try {
    _loadFiles.value = true;
    const listFile = await apiFileUtils.listFileStore(insuranceId.value);
    if (_key.value !== key) {
      return;
    }
    if (listFile) {
      _tempFile.value =
        listFile?.sort((a, b) =>
          a.date && b.date
            ? Date.parse(b.date.toString()) - Date.parse(a.date.toString())
            : 0
        ) ?? [];
    }
  } finally {
    _loadFiles.value = false;
  }
}

onMounted(async () => {
  await p_init();
});

onBeforeRouteUpdate(async () => {
  await p_init();
});

async function onPrint(policyHolder: boolean) {
  if (!currentInsuranceId.value) return;
  if (_insurance.value.insuranceType === InsuranceType.Zoll) {
    await _navigationStore.showPrintExportInsurance([_insurance.value])
    return
  }
  try {
    _spinner.show();
    const id = await apiInsurance.initPdf([currentInsuranceId.value], policyHolder);
    if (id) {
      await apiFile.downloadFile(id);
    }
  } finally {
    _spinner.hide();
  }
}

async function onImport() {
  if (!_tempFileInfo.value || !_tempFileInfo.value.id) return;
  const res = await apiInsurance.addTempFile(
    _tempFileInfo.value.id,
    _insurance.value.id,
    _tempFileInfo.value.name,
    _tempFileInfo.value.fileName
  );
  if (res) {
    notificationService.success(
      "Datei " + _tempFileInfo.value.name + " wurde gespeichert"
    );
    _tempFileInfo.value = undefined;
    _uploader.value?.clear();
    _uploadTempFile.value = false;
    await p_init();

    _spinner.hide();
  }
}

function onTakePhoto() {
  _uploadTempFile.value = true;
  _camera.value = true;
  _uploader.value?.takePhoto();
}

function onDelete(file: ListFile) {
  notificationService.showYesNo(
    "Datei löschen",
    "Soll die Datei " + file.name + " wirklich gelöscht werden?",
    async () => {
      const res = await apiFileUtils.deleteFile(file?.id ?? "");
      if (res) {
        notificationService.success("Datei " + file.name + " wurde gelöscht");
        _tempFile.value = _tempFile.value.filter((f) => f.id !== file.id);
      } else
        notificationService.error(
          "Datei " + file.name + " konnte nicht gelöscht werden"
        );
    }
  );
}

async function onDownload(file: ListFile) {
  if (!file.personId) return;
  try {
    _spinner.show();
    const fileId = await apiFileUtils.initDownloadFile(file.id ?? "");
    if (fileId) await apiFile.downloadTempFile(fileId);
  } finally {
    _spinner.hide();
  }
}

async function onMoreSelect(id: string) {
  if (id === "printWithout") {
    await onPrint(false)
  }
}

const _cancelVisible = computed(() => {
  if (_insurance.value?.vehicleRegistrationId) {
    return false;
  }
  if (_insurance.value.evbValidityDate && addDays(new Date(), 1) < (new Date(_insurance.value.evbValidityDate))) {
    return false;
  }
  return true;
})


function onCancel() {
  notificationService.showYesNo(
    "Versicherungskarte stornieren",
    "Möchten Sie die Versicherungskarte wirklich stornieren?",
    async () => {
      const res = await apiInsurance.insuranceCanclel(insuranceId.value ?? "");
      if (res) {
        notificationService.success("Stornirungsanfrage wurde erstellt.");
        await p_init()
      }
    }
  );
}

</script>
<style scoped></style>
